import './BrandFilters.css';
import { useState, useEffect } from 'react';
import { HiOutlineX, HiOutlineFilter } from 'react-icons/hi';
import MultiSelectDropdown from '../MultiSelectDropdown/MultiSelectDropdown';
import { UltimateParentFilters, useOpportunityStore } from '../../stores';

interface BrandFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters?: (filters: UltimateParentFilters) => void;
}

function BrandFilters({ isOpen, onClose, onApplyFilters }: BrandFiltersProps) {
  const opportunityStore = useOpportunityStore();
  const [retailerSegments, setRetailerSegments] = useState<string[]>([]);
  const [opportunitySize, setOpportunitySize] = useState('All');

  useEffect(() => {
    const storeFilters = opportunityStore.filters;

    if (storeFilters.retailerSegment) {
      setRetailerSegments(Array.isArray(storeFilters.retailerSegment) ? storeFilters.retailerSegment : [storeFilters.retailerSegment]);
    } else {
      setRetailerSegments([]);
    }

    if (storeFilters.opportunitiesMin === 0 && storeFilters.opportunitiesMax === 100) {
      setOpportunitySize('Low (0-100)');
    } else if (storeFilters.opportunitiesMin === 100 && storeFilters.opportunitiesMax === 1000) {
      setOpportunitySize('Medium (100-1000)');
    } else if (storeFilters.opportunitiesMin === 1000 && storeFilters.opportunitiesMax === 999999999) {
      setOpportunitySize('High (1000+)');
    } else {
      setOpportunitySize('All');
    }
  }, [isOpen, opportunityStore.filters]);

  const retailerSegmentOptions = [
    'Top 100 Retailers',
    'Next 100 Retailers',
    'Emerging',
    'Independent'
  ];

  const activeFilterCount =
    (retailerSegments.length > 0 ? 1 : 0) +
    (opportunitySize !== 'All' ? 1 : 0);

  const handleClearFilters = () => {
    setRetailerSegments([]);
    setOpportunitySize('All');
  };

  const mapOpportunitySizeToRange = (size: string): { min: number; max: number } => {
    switch (size) {
      case 'Low (0-100)':
        return { min: 0, max: 100 };
      case 'Medium (100-1000)':
        return { min: 100, max: 1000 };
      case 'High (1000+)':
        return { min: 1000, max: 999999999 };
      default:
        return { min: 0, max: 999999999 };
    }
  };

  const handleApplyFilters = () => {
    const opportunityRange = mapOpportunitySizeToRange(opportunitySize);

    const appliedFilters: UltimateParentFilters = {
      retailerSegment: retailerSegments.length > 0 ? retailerSegments : null,
      opportunitiesMin: opportunityRange.min,
      opportunitiesMax: opportunityRange.max,
      retailerSitesMin: 0,
      retailerSitesMax: 999999999,
    };

    if (onApplyFilters) {
      onApplyFilters(appliedFilters);
    }
  };

  return (
    <>
      {isOpen && <div className="filter-config-overlay" onClick={onClose}></div>}
      <div className={`filter-config-panel ${isOpen ? 'open' : ''}`}>
        <div className="filter-config-header">
          <div className="header-left-group">
            <div className="header-icon">
              <HiOutlineFilter />
            </div>
            <div className="header-left-f">
              <h2>Filters</h2>
              <p className="header-subtitle">Apply filters to refine your opportunities</p>
            </div>
            {activeFilterCount > 0 && (
              <span className="active-filters-badge">{activeFilterCount}</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {activeFilterCount > 0 && (
          <div className="clear-filters-section">
            <button className="clear-all-filters-btn" onClick={handleClearFilters}>
              <HiOutlineX />
              Clear All Filters
            </button>
          </div>
        )}

        <div className="filter-config-content">
          <div className="filters-tab">
            {activeFilterCount > 0 && (
              <div className="active-filters-display">
                <div className="active-filters-title">Active Filters</div>
                <div className="active-filters-list">
                  {retailerSegments.length > 0 && (
                    <span className="active-filter-chip">
                      Retailer Segment: {retailerSegments.join(', ')}
                      <button
                        className="filter-chip-remove"
                        onClick={() => setRetailerSegments([])}
                        aria-label="Remove Retailer Segment filter"
                      >
                        <HiOutlineX />
                      </button>
                    </span>
                  )}
                  {opportunitySize !== 'All' && (
                    <span className="active-filter-chip">
                      Opportunity: {opportunitySize}
                      <button
                        className="filter-chip-remove"
                        onClick={() => setOpportunitySize('All')}
                        aria-label="Remove Opportunity Size filter"
                      >
                        <HiOutlineX />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="filter-section">
              <div className="section-header">
                <h3>Retailer Segment</h3>
                {retailerSegments.length > 0 && (
                  <span className="active-filter-tag">{retailerSegments.length}</span>
                )}
              </div>
              <div className="filter-fields">
                <div className="filter-field">
                  <MultiSelectDropdown
                    values={retailerSegments}
                    options={retailerSegmentOptions}
                    onChange={setRetailerSegments}
                    placeholder="Select retailers"
                  />
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button className="close-panel-btn" onClick={onClose}>
                Close
              </button>
              <button className="apply-filters-btn" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BrandFilters;
